"use client"

import { useRef } from "react"
import { motion, useInView } from "framer-motion"
import { Phone, MapPin, Clock, CheckCircle2 } from "lucide-react"
import ContactForm from "@/components/ContactForm"

const CTASection = () => {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: "-10% 0px" })

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="relative overflow-hidden px-4 sm:px-6 lg:px-8 py-12 md:py-16"
      style={{ background: "hsl(222, 48%, 10%)" }}
    >
      {/* Animated background gradient */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0 }}
        animate={{ opacity: isInView ? 1 : 0 }}
        transition={{ duration: 1.6, ease: "easeOut" }}
        style={{
          background: `linear-gradient(
            to bottom,
            hsl(222, 48%, 14%) 0%,
            hsl(222, 48%, 10%) 40%,
            hsl(222, 48%, 7%) 100%
          )`,
        }}
      />

      {/* Shimmer overlay */}
      <div className="sunset-shimmer absolute inset-0" />

      <div className="relative z-10 max-w-6xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">

          {/* Left — info */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: isInView ? 1 : 0, x: isInView ? 0 : -24 }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <p className="text-sm font-medium tracking-[0.18em] uppercase text-[hsl(var(--terracotta))] mb-3">
              Start Your Journey
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-white mb-5 leading-snug">
              Ready to Take the{" "}
              <em className="not-italic text-[hsl(var(--sage-light))]">First Step?</em>
            </h2>
            <div className="w-14 h-0.5 bg-[hsl(var(--sage))] mb-8" />

            <p className="text-base text-white/75 leading-relaxed mb-8">
              Reaching out is often the hardest part. Our team is here to make it easy — no
              pressure, no judgment. Just a warm conversation about how we can support you or
              someone you care about.
            </p>

            <ul className="space-y-4 mb-8">
              {[
                "Confidential and judgment-free consultations",
                "Licensed trauma therapists and EMDR specialists",
                "Flexible scheduling including evenings and weekends",
                "Insurance accepted — BCBS, Aetna, Medicaid, and more",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-[hsl(var(--sage))] flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>

            <div className="space-y-4">
              <a
                href="tel:3363105098"
                className="flex items-center gap-3 text-sm text-white/70 hover:text-white transition-colors"
              >
                <Phone className="w-4 h-4 text-[hsl(var(--sage))]" />
                336.310.5098
              </a>
              <div className="flex items-center gap-3 text-sm text-white/70">
                <MapPin className="w-4 h-4 text-[hsl(var(--sage))]" />
                Serving individuals throughout North Carolina
              </div>
              <div className="flex items-center gap-3 text-sm text-white/70">
                <Clock className="w-4 h-4 text-[hsl(var(--sage))]" />
                Mon–Fri 9am–6pm · Sat by appointment
              </div>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: isInView ? 1 : 0, x: isInView ? 0 : 24 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl p-7 md:p-9 shadow-xl">
              <ContactForm
                variant="dark"
                successMode="inline"
                formSource="home-cta"
              />
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  )
}

export default CTASection
